import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Calendar as CalendarIcon,
  Plus,
  Monitor,
  Image as ImageIcon,
  Trash2,
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';

export default function SchedulePage() {
  const { user, isAdmin, isOperator } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [screens, setScreens] = useState([]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    screen_id: '',
    content_id: '',
    campaign_id: '',
    start_time: '',
    end_time: '',
    repeat: 'once',
    days_of_week: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [schedulesRes, screensRes, contentRes] = await Promise.all([
        api.get('/schedules'),
        api.get('/screens'),
        api.get('/content'),
      ]);
      setSchedules(schedulesRes.data);
      setScreens(screensRes.data);
      setContent(contentRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/schedules', formData);
      toast.success('Schedule created successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success('Schedule deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const resetForm = () => {
    setFormData({
      screen_id: '',
      content_id: '',
      campaign_id: '',
      start_time: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      end_time: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      repeat: 'once',
      days_of_week: [],
    });
  };

  const getRepeatBadge = (repeat) => {
    const styles = {
      once: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
      daily: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      weekly: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    };
    return (
      <Badge variant="outline" className={styles[repeat] || styles.once}>
        {repeat}
      </Badge>
    );
  };

  // Get schedules for selected date
  const getSchedulesForDate = (date) => {
    return schedules.filter((schedule) => {
      const scheduleDate = new Date(schedule.start_time);
      return isSameDay(scheduleDate, date);
    });
  };

  const selectedDateSchedules = getSchedulesForDate(selectedDate);

  // Days with schedules
  const daysWithSchedules = schedules.map((s) => new Date(s.start_time));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin && !isOperator) {
    return (
      <div className="glass-card rounded-xl p-12 text-center">
        <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
        <h3 className="text-xl font-semibold text-white mb-2">Access Restricted</h3>
        <p className="text-zinc-400">Only operators and administrators can manage schedules.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Schedule
          </h1>
          <p className="text-zinc-400 mt-1">Plan your content display times</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-500 btn-glow"
          data-testid="add-schedule-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Schedule
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="glass-card border-zinc-800 lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              {format(currentMonth, 'MMMM yyyy')}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              className="rounded-md"
              modifiers={{
                hasSchedule: daysWithSchedules,
              }}
              modifiersStyles={{
                hasSchedule: {
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  textDecorationColor: '#3b82f6',
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Selected Date Schedules */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              {format(selectedDate, 'EEEE, MMM d')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedDateSchedules.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 mx-auto text-zinc-700 mb-3" />
                <p className="text-zinc-500">No schedules for this day</p>
                <Button
                  variant="outline"
                  className="mt-4 border-zinc-700"
                  onClick={() => {
                    resetForm();
                    setFormData((prev) => ({
                      ...prev,
                      start_time: format(selectedDate, "yyyy-MM-dd'T'09:00"),
                      end_time: format(selectedDate, "yyyy-MM-dd'T'18:00"),
                    }));
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Schedule
                </Button>
              </div>
            ) : (
              selectedDateSchedules.map((schedule) => (
                <div
                  key={schedule.id}
                  className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors"
                  data-testid={`schedule-item-${schedule.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-white">{schedule.content_name}</p>
                      <div className="flex items-center gap-1 text-sm text-zinc-500 mt-0.5">
                        <Monitor className="w-3 h-3" />
                        {schedule.screen_name}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-zinc-500 hover:text-rose-400"
                      onClick={() => handleDelete(schedule.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-xs text-zinc-400">
                      <Clock className="w-3 h-3" />
                      {format(new Date(schedule.start_time), 'HH:mm')} - {format(new Date(schedule.end_time), 'HH:mm')}
                    </div>
                    {getRepeatBadge(schedule.repeat)}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Schedules List */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            All Scheduled Content
          </CardTitle>
        </CardHeader>
        <CardContent>
          {schedules.length === 0 ? (
            <div className="text-center py-8">
              <CalendarIcon className="w-12 h-12 mx-auto text-zinc-700 mb-3" />
              <p className="text-zinc-500">No schedules yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {schedules.map((schedule) => (
                <div
                  key={schedule.id}
                  className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{schedule.content_name}</p>
                        <p className="text-sm text-zinc-500">{schedule.screen_name}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-zinc-500 hover:text-rose-400"
                      onClick={() => handleDelete(schedule.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-zinc-400">
                      {format(new Date(schedule.start_time), 'MMM d, HH:mm')} - {format(new Date(schedule.end_time), 'HH:mm')}
                    </div>
                    {getRepeatBadge(schedule.repeat)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Schedule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Create Schedule
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-300">Screen</Label>
              <Select
                value={formData.screen_id}
                onValueChange={(value) => setFormData({ ...formData, screen_id: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="schedule-screen-select">
                  <SelectValue placeholder="Select screen" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {screens.map((screen) => (
                    <SelectItem key={screen.id} value={screen.id}>
                      {screen.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-300">Content</Label>
              <Select
                value={formData.content_id}
                onValueChange={(value) => setFormData({ ...formData, content_id: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="schedule-content-select">
                  <SelectValue placeholder="Select content" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {content.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Start Time</Label>
                <input
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-300">End Time</Label>
                <input
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white mt-1"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-300">Repeat</Label>
              <Select
                value={formData.repeat}
                onValueChange={(value) => setFormData({ ...formData, repeat: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="once">Once</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-500" data-testid="save-schedule-btn">
                Create Schedule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
